import {
  HumanDocument,
  OrganizationDocument,
  UserDocument,
  InsuranceCaseDocument,
  MatterData,
  LeadSource,
  MatterStatus,
  FeeEarned,
  EngagementLetterDocument,
  ContactRole,
  MatterType,
  SchedulingEmailSent,
} from '@shared/types';

import { DocumentReference } from 'firebase/firestore';

export class FlattenedMatter {
  // Required fields
  contacts: EnrichedMatterContact[];
  description: string;
  id: string;
  matterRef: DocumentReference<MatterData>;
  matterType: MatterType;
  name: string;
  state: string;
  status: MatterStatus;

  // Optional fields
  /**
   * Maps organization id to an array of human ids representing representatives for that org in this matter.
   * Example: { 'org-123': ['human-1', 'human-2'] }
   */
  organizationRepresentatives: Record<string, string[]> | undefined;
  createdTimestamp: Date | undefined;
  engagementLetters: EngagementLetterDocument[] | undefined;
  engagementOriginator: UserDocument | undefined;
  // Mirrors `MatterData.flatFeeAmt` / `MatterData.retainer`: stored as a
  // string for legacy matters and a number for newer ones (the form's
  // NumberInput writes a number directly).
  flatFeeAmt: string | number | undefined;
  leadSource: LeadSource | undefined;
  insuranceCases: InsuranceCaseDocument[] | undefined;
  isUninvoiceable: true | undefined;
  #primaryClient: EnrichedMatterContact | undefined;
  #primaryAdverseParty: EnrichedMatterContact | undefined;
  practiceArea: string | undefined;
  referralOriginator: UserDocument | undefined;
  responsibleAttorney: UserDocument | undefined;
  otherAssignee: UserDocument | undefined;
  responsibleAttyHourlyRate: string | undefined;
  retainer: string | number | undefined;
  scope: string | undefined;
  schedulingEmailSent: SchedulingEmailSent | undefined;
  subStatusId: string | undefined;
  trustBalance: number | undefined;
  feeEarned: FeeEarned[] | undefined;
  contingencyFees:
    | {
        pctPreFilingOfLawsuit?: number;
        pctUponFilingOfLawsuit?: number;
        pctUponCommencementOfTrial?: number;
      }
    | undefined;

  constructor(dict: {
    // Required fields
    contacts: EnrichedMatterContact[];
    description: string;
    id: string;
    matterRef: DocumentReference<MatterData>;
    matterType: MatterType;
    name: string;
    state: string;
    status: MatterStatus;

    // Optional fields
    organizationRepresentatives: Record<string, string[]> | undefined;
    createdTimestamp: Date | undefined;
    engagementLetters: EngagementLetterDocument[] | undefined;
    engagementOriginator: UserDocument | undefined;
    flatFeeAmt: string | number | undefined;
    leadSource: LeadSource | undefined;
    insuranceCases: InsuranceCaseDocument[] | undefined;
    isUninvoiceable?: true;
    practiceArea: string | undefined;
    primaryClient: EnrichedMatterContact | undefined;
    primaryAdverseParty: EnrichedMatterContact | undefined;
    referralOriginator: UserDocument | undefined;
    responsibleAttorney: UserDocument | undefined;
    otherAssignee: UserDocument | undefined;
    responsibleAttyHourlyRate: string | undefined;
    retainer: string | number | undefined;
    scope: string | undefined;
    schedulingEmailSent?: SchedulingEmailSent | undefined;
    subStatusId: string | undefined;
    trustBalance: number | undefined;
    feeEarned: FeeEarned[] | undefined;
    contingencyFees:
      | {
          pctPreFilingOfLawsuit?: number;
          pctUponFilingOfLawsuit?: number;
          pctUponCommencementOfTrial?: number;
        }
      | undefined;
  }) {
    // Required fields
    this.contacts = dict.contacts;
    this.description = dict.description;
    this.id = dict.id;
    this.matterRef = dict.matterRef;
    this.matterType = dict.matterType;
    this.name = dict.name;
    this.state = dict.state;
    this.status = dict.status;

    // Optional properties
    this.organizationRepresentatives = dict.organizationRepresentatives;
    this.insuranceCases = dict.insuranceCases;
    this.isUninvoiceable = dict.isUninvoiceable;
    this.createdTimestamp = dict.createdTimestamp;
    this.engagementOriginator = dict.engagementOriginator;
    this.flatFeeAmt = dict.flatFeeAmt;
    this.leadSource = dict.leadSource;
    this.insuranceCases = dict.insuranceCases;
    this.#primaryClient = dict.primaryClient;
    this.#primaryAdverseParty = dict.primaryAdverseParty;
    this.practiceArea = dict.practiceArea;
    this.referralOriginator = dict.referralOriginator;
    this.responsibleAttorney = dict.responsibleAttorney;
    this.otherAssignee = dict.otherAssignee;
    this.responsibleAttyHourlyRate = dict.responsibleAttyHourlyRate;
    this.retainer = dict.retainer;
    this.scope = dict.scope;
    this.schedulingEmailSent = dict.schedulingEmailSent;
    this.engagementLetters = dict.engagementLetters;
    this.subStatusId = dict.subStatusId;
    this.trustBalance = dict.trustBalance;
    this.feeEarned = dict.feeEarned;
    this.contingencyFees = dict.contingencyFees;
  }

  get clients(): (EnrichedHumanDocument | EnrichedOrganizationDocument)[] {
    return this.contacts.filter((client) => {
      if (client.role !== 'client') {
        return false;
      }
      return true;
    });
  }

  get humanClients(): EnrichedHumanDocument[] {
    return this.contacts.filter((client) => {
      if (client.type !== 'human') {
        return false;
      }
      if (client.role !== 'client') {
        return false;
      }
      return true;
    }) as EnrichedHumanDocument[];
  }

  get organizationClients(): EnrichedOrganizationDocument[] {
    return this.contacts.filter((client) => {
      if (client.type !== 'organization') {
        return false;
      }
      if (client.role !== 'client') {
        return false;
      }
      return true;
    }) as EnrichedOrganizationDocument[];
  }

  get primaryClient():
    | EnrichedHumanDocument
    | EnrichedOrganizationDocument
    | undefined {
    if (this.#primaryClient) {
      return this.#primaryClient;
    }
    if (this.humanClients.length > 0) {
      return this.humanClients[0];
    }
    if (this.organizationClients.length > 0) {
      return this.organizationClients[0];
    }
    return undefined;
  }

  get otherClients(): (EnrichedHumanDocument | EnrichedOrganizationDocument)[] {
    const clients: (EnrichedHumanDocument | EnrichedOrganizationDocument)[] = [
      ...this.humanClients,
      ...this.organizationClients,
    ];
    if (clients.length > 1) {
      const remainingClients = clients.filter((client) => {
        return client.id !== this.primaryClient?.id;
      });
      return remainingClients;
    }
    return [];
  }

  get clientCompanyNames(): string | undefined {
    if (this.organizationClients.length > 0) {
      return this.organizationClients.map((org) => org.companyName).join(', ');
    }
    return undefined;
  }

  get adverseParties(): (
    | EnrichedHumanDocument
    | EnrichedOrganizationDocument
  )[] {
    return this.contacts.filter((party) => {
      if (party.role !== 'adverseParty') {
        return false;
      }
      return true;
    });
  }

  get primaryAdverseParty():
    | EnrichedHumanDocument
    | EnrichedOrganizationDocument
    | undefined {
    if (this.#primaryAdverseParty) {
      return this.#primaryAdverseParty;
    }
    if (this.humanAdverseParties.length > 0) {
      return this.humanAdverseParties[0];
    }
    if (this.organizationAdverseParties.length > 0) {
      return this.organizationAdverseParties[0];
    }
    return undefined;
  }

  get humanAdverseParties(): EnrichedHumanDocument[] {
    return this.adverseParties.filter((party) => {
      if (party.type !== 'human') {
        return false;
      }
      return true;
    }) as EnrichedHumanDocument[];
  }

  get organizationAdverseParties(): EnrichedOrganizationDocument[] {
    return this.adverseParties.filter((party) => {
      if (party.type !== 'organization') {
        return false;
      }
      return true;
    }) as EnrichedOrganizationDocument[];
  }

  get adversePartyCompanyNames(): string | undefined {
    if (this.organizationAdverseParties.length > 0) {
      return this.organizationAdverseParties
        .map((org) => org.companyName)
        .join(', ');
    }
    return undefined;
  }
}

export type EnrichedMatterContact =
  | EnrichedHumanDocument
  | EnrichedOrganizationDocument;

export type EnrichedHumanDocument = HumanDocument & {
  type: 'human';
  role: ContactRole;
};
export type EnrichedOrganizationDocument = OrganizationDocument & {
  type: 'organization';
  role: ContactRole;
};
